// Global WebSocket manager that survives HMR
import logger from './logger.js';

class WebSocketManager {
  constructor() {
    this.ws = null;
    this.reconnectTimeout = null;
    this.reconnectAttempts = 0;
    this.messageHandlers = new Set();
    this.isConnecting = false;
  }

  connect() {
    if (this.isConnecting) {
      logger.debug('WEBSOCKET', 'Connection already in progress, skipping');
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      logger.debug('WEBSOCKET', 'WebSocket already connected, skipping');
      return;
    }

    logger.info('WEBSOCKET', 'Connecting to WebSocket');
    this.isConnecting = true;

    // Use secure WebSocket for production, insecure for development
    const isProduction = import.meta.env.MODE === 'production';
    const wsUrl = isProduction ? 'wss://grotto.fm' : 'ws://localhost:4000';

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      logger.info('WEBSOCKET', 'WebSocket connected successfully');
      this.reconnectAttempts = 0;
      this.isConnecting = false;
    };

    this.ws.onmessage = (event) => {
      // Broadcast to all registered handlers
      this.messageHandlers.forEach(handler => {
        try {
          handler(event);
        } catch (error) {
          logger.error('WEBSOCKET', 'Error in message handler', { error: error.message });
        }
      });
    };

    this.ws.onclose = (event) => {
      logger.warn('WEBSOCKET', 'WebSocket disconnected', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      this.isConnecting = false;
      this.scheduleReconnect();
    };

    this.ws.onerror = (err) => {
      logger.error('WEBSOCKET', 'WebSocket error', { error: err.type });
      this.isConnecting = false;
      this.ws.close();
    };
  }

  disconnect() {
    logger.info('WEBSOCKET', 'Disconnecting WebSocket');
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnecting = false;
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
      return true;
    } else {
      logger.warn('WEBSOCKET', 'Cannot send message - WebSocket not open');
      return false;
    }
  }

  addMessageHandler(handler) {
    this.messageHandlers.add(handler);
  }

  removeMessageHandler(handler) {
    this.messageHandlers.delete(handler);
  }

  scheduleReconnect() {
    // Don't schedule reconnection if we've already tried too many times
    if (this.reconnectAttempts >= 5) {
      logger.warn('WEBSOCKET', 'Max reconnection attempts reached, stopping reconnection attempts');
      return;
    }

    const delay = Math.min(10000, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts++;
    logger.info('WEBSOCKET', `Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);
    this.reconnectTimeout = setTimeout(() => this.connect(), delay);
  }

  getReadyState() {
    return this.ws ? this.ws.readyState : WebSocket.CLOSED;
  }
}

// Create singleton instance
const wsManager = new WebSocketManager();

export default wsManager;